import React, { memo, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { ClickAwayListener, Popper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { combinedColor, OPACITY_LIGHTEST, PLAIN_GREY, WHITE } from '~/Constants/Colors.constants';
import { EventType } from '~/Models/DTO/Event.model';
import { selectTrainersMap } from '~/store/provider';
import { makeStyles } from '~/UIKit/Style';
import { useCalendarEvent } from './useCalendarEvent';
import { BOX_SHADOW_HOVER, BOX_SHADOW_TOOLTIP_1 } from '~/Constants/CSS.constants';
import cn from 'classnames';
import { EVENT_Z_INDEX } from '~/Screens/Calendar/constants';
import { ICalendarEvent } from '~/Screens/Calendar/interfaces';

interface ICalendarEventItemProps {
  event: ICalendarEvent;
  withTooltip?: boolean;
}

interface IUseStyle {
  color: string;
}

const borderDashSize = 7;

const useStyles = ({ color }: IUseStyle) =>
  makeStyles(
    {
      event: {
        height: '100%',
        overflow: 'hidden',
      },
      eventItemWrapper: {
        display: 'flex',
        maxWidth: '100%',
        backgroundColor: WHITE,
        fontSize: '12px',
        position: 'relative',
        borderRadius: 0,
        height: '100%',
        borderTop: `1px solid ${WHITE}`,
        borderLeft: `2.5px solid ${color}`,
        borderBottom: `1px solid ${WHITE}`,
        userSelect: 'none',
      },
      newEvent: {
        ':before': {
          content: '""',
          width: '100%',
          height: '100%',
          position: 'absolute',
          backgroundPosition: '0 0, 100% 0, 0 100%',
          backgroundSize: '100% 1px, 1px 100% , 100% 1px',
          backgroundRepeat: 'no-repeat',
          borderImage: `repeating-linear-gradient(0deg, ${color}, ${color} ${borderDashSize}px, transparent ${borderDashSize}px, transparent ${
            borderDashSize * 2
          }px)`,
          backgroundImage: `
          repeating-linear-gradient(90deg, ${color}, ${color} ${borderDashSize}px, transparent ${borderDashSize}px, transparent ${
            borderDashSize * 2
          }px), // top
          repeating-linear-gradient(180deg, ${color}, ${color} ${borderDashSize}px, transparent ${borderDashSize}px, transparent ${
            borderDashSize * 2
          }px), // right
          repeating-linear-gradient(270deg, ${color}, ${color} ${borderDashSize}px, transparent ${borderDashSize}px, transparent ${
            borderDashSize * 2
          }px) // bottom`,
        },
      },
      title: {
        fontWeight: 'bold',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
      detailsWrapper: {
        cursor: 'inherit',
        backgroundColor: combinedColor(color, OPACITY_LIGHTEST),
        width: '100%',
        paddingLeft: '4px',
        paddingRight: '4px',
        '&:hover': {
          boxShadow: BOX_SHADOW_HOVER,
        },
      },
      detail: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
      popper: {
        zIndex: EVENT_Z_INDEX + 2,
        boxShadow: BOX_SHADOW_TOOLTIP_1,
      },
      popperContent: {
        width: '100%',
        borderTop: 0,
        borderBottom: 0,
      },
      cancelled: {
        color: PLAIN_GREY,
      },
    },
    { name: 'CalendarEvent' },
  );

export const CalendarEvent = memo(({ event, withTooltip }: ICalendarEventItemProps) => {
  const { goToDetail } = useCalendarEvent({ event });
  const { t } = useTranslation();
  const { classes } = useStyles({ color: event.color })();
  const trainersMap = useSelector(selectTrainersMap);
  const trainer = event.trainerIds?.[0] ? trainersMap[event.trainerIds[0]] : null;
  const anchorEl = useRef(null);
  const textRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const togglePopover = (shouldOpen: boolean) => () => {
    if (shouldOpen !== open) {
      setOpen(shouldOpen);
    }
  };
  const eventDetails = useMemo(() => {
    const eventStartDate = format(new Date(event.start), 'HH:mm');
    const eventEndDate = format(new Date(event.end), 'HH:mm');
    const trainerName = trainer?.name ? ` · ${trainer?.name}` : '';

    if (event.type === EventType.FREE_TRAINING) {
      return `${eventStartDate}-${eventEndDate} · ${t('event.freeTraining')}`;
    }

    return `${eventStartDate}-${eventEndDate}${trainerName}`;
  }, [event, trainer]);

  const isNotFullyDisplayed = [anchorEl, textRef, descriptionRef].some(
    (ref) => ref.current && ref.current.offsetWidth < ref.current.scrollWidth,
  );

  return (
    <>
      {withTooltip && isNotFullyDisplayed && (
        <Popper
          onResize={undefined}
          onResizeCapture={undefined}
          nonce={undefined}
          open={open}
          anchorEl={anchorEl.current}
          placement="top"
          disablePortal={false}
          className={classes.popper}
          modifiers={[
            {
              name: 'flip',
              enabled: true,
              options: {
                altBoundary: true,
                rootBoundary: 'document',
                padding: 5,
              },
            },
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                padding: 5,
                rootBoundary: 'document',
              },
            },
          ]}
        >
          <ClickAwayListener onClickAway={togglePopover(false)}>
            <div className={cn(classes.eventItemWrapper, classes.popperContent)}>
              <div className={cn(classes.detailsWrapper, { [classes.cancelled]: event.cancelled })}>
                <div className={classes.title}>{event.name || t('event.createNew')}</div>
                <div className={classes.detail}>{eventDetails}</div>
                {event.cancelled && t('app.event.cancelled')}
              </div>
            </div>
          </ClickAwayListener>
        </Popper>
      )}
      <div
        className={classes.event}
        onMouseDown={togglePopover(false)}
        onMouseEnter={togglePopover(true)}
        onMouseLeave={togglePopover(false)}
        ref={anchorEl}
      >
        <div
          onClick={goToDetail}
          className={cn(classes.eventItemWrapper, classes.eventItemWrapperMainContent, {
            [classes.newEvent]: !event.id,
          })}
        >
          <div className={cn(classes.detailsWrapper, { [classes.cancelled]: event.cancelled })}>
            <div className={classes.title} ref={textRef}>
              {event.name || t('event.createNew')}
            </div>
            <div className={classes.detail} ref={descriptionRef}>
              {eventDetails}
            </div>
            {event.cancelled && t('app.event.cancelled')}
          </div>
        </div>
      </div>
    </>
  );
});
